import type { AgentsTranslations } from '../interfaces/agents';

export const agentsTranslations: AgentsTranslations = {
  page_title: 'Agents',
  page_description:
    'Manage the AI agents that respond on your behalf. Create one per use case and tune the configuration to your business.',
  empty_title: 'No agents yet',
  empty_description:
    'Create your first agent to start automating conversations with your customers.',
  create_button: 'Create agent',
  back_to_list: 'Back to agents',

  agent_type_ecommerce: 'E-commerce',
  agent_type_real_estate: 'Real estate',
  agent_type_fintech: 'Fintech',
  template_ecommerce_default: 'E-commerce defaults',
  template_real_estate_default: 'Real estate defaults',
  template_fintech_default: 'Fintech defaults',
  template_empty: 'Empty',

  status_active: 'Active',
  status_inactive: 'Inactive',
  status_draft: 'Draft',

  card_actions_label: 'Agent actions',
  action_edit: 'Edit',
  action_duplicate: 'Duplicate',
  action_activate: 'Activate',
  action_deactivate: 'Deactivate',
  action_mark_draft: 'Move to draft',
  action_unmark_draft: 'Move out of draft',
  action_delete: 'Delete',

  create_page_title: 'Create new agent',
  create_page_description:
    'Pick a name and a starting template. You can fine-tune everything afterwards.',
  create_tab_manual: 'Manual',
  create_tab_files: 'Upload files',
  create_field_name_label: 'Display name',
  create_field_name_placeholder: 'e.g. My Store – WhatsApp',
  create_field_name_help: 'Must be unique within your organization.',
  create_field_type_label: 'Agent type',
  create_field_template_label: 'Starting template',
  create_field_template_help:
    'The template populates default values; you can override anything later.',
  create_submit: 'Create agent',
  create_files_section_title: 'Source files (optional)',
  create_files_section_description:
    'Drop documents (PDF, DOCX, TXT, MD, CSV) that describe your business. Used to pre-fill the configuration once the ingestion pipeline is live.',
  create_files_pending_notice:
    'File processing is staged in this release. Files stay attached to the draft and will be ingested when the backend pipeline is enabled.',

  dropzone_idle: 'Drag files here or click to browse',
  dropzone_active: 'Drop the files to attach them',
  dropzone_hint: 'PDF, DOCX, TXT, MD, CSV — up to 10 files, 20 MB each.',
  dropzone_remove: 'Remove file',
  dropzone_size_unit_b: 'B',
  dropzone_size_unit_kb: 'KB',
  dropzone_size_unit_mb: 'MB',

  duplicate_dialog_title: 'Duplicate agent',
  duplicate_dialog_description:
    'Pick a new display name. The full configuration is copied; the duplicate starts active.',
  duplicate_dialog_field_label: 'New display name',
  duplicate_dialog_confirm: 'Duplicate',
  duplicate_dialog_cancel: 'Cancel',

  delete_dialog_title: 'Delete this agent?',
  delete_dialog_description:
    'The agent stops handling messages immediately. Soft-deleted; the display name is not freed.',
  delete_dialog_confirm: 'Delete agent',
  delete_dialog_cancel: 'Cancel',

  edit_page_loading: 'Loading agent…',
  edit_page_not_found_title: 'Agent not found',
  edit_page_not_found_description:
    'It may have been deleted or you may not have access. Go back to the list and try again.',
  edit_save: 'Save changes',
  edit_saving: 'Saving…',
  edit_discard: 'Discard changes',
  edit_no_changes_hint: 'No unsaved changes.',
  edit_field_name_label: 'Display name',
  edit_schema_loading: 'Loading configuration schema…',
  edit_schema_error: 'We could not load the configuration schema.',
  edit_schema_retry: 'Retry',
  edit_view_mode_form: 'Form',
  edit_view_mode_json: 'JSON',
  edit_nav_search_placeholder: 'Search sections…',
  edit_nav_group_label: 'Agent configuration',
  edit_test_with_customer: 'Test with customer',
  edit_test_with_customer_coming_soon: 'Coming soon',
  edit_view_in_production: 'View in production',
  edit_view_in_production_inactive: 'Activate the agent first',
  edit_breadcrumb_root: 'Agents',
  edit_unsaved_pill: 'Unsaved changes',

  hero_agent_label: 'Agent',
  hero_agent_of: 'Agent of',
  hero_subtitle:
    'Everything you can configure for this agent: identity, model, tone, data, goals, handoffs and follow-ups.',
  identity_sandbox: 'Test in sandbox',
  identity_timezone_label: 'Timezone',
  identity_timezone_placeholder: 'Pick a timezone…',
  identity_status_label: 'Status',
  identity_status_help: 'Use the actions menu to activate or pause.',
  identity_status_inline_hint:
    'Status is managed from the actions menu in the top bar.',

  llm_main_title: 'Primary model',
  llm_model_label: 'Model',
  llm_model_placeholder: 'Select a model…',
  llm_overrides_title: 'Per-task overrides',
  llm_overrides_description:
    'Use a different model for specific tasks (classification, extraction, profiling).',
  llm_overrides_placeholder:
    'Per-task overrides will be available soon. The main model will be used for every task in the meantime.',

  tone_preferred_title: 'Preferred words',
  tone_preferred_help: 'Brand vocabulary the agent should prioritize.',
  tone_forbidden_title: 'Forbidden words',
  tone_forbidden_help: 'Always avoided and blocked from responses.',
  tone_guard_title: 'In-scope topics',
  tone_guard_help:
    'Subjects the guardrail should NOT block. The agent will engage with these even if they look off-topic at first glance.',
  field_guard_topics_label: 'In-scope topics',
  field_guard_topics_help:
    'List of subjects the guard treats as on-topic. Useful when customers raise emotional or tangential issues that still belong to the brand context (e.g. "love", "money", "anxiety").',

  examples_add: 'Add example',
  examples_question_label: 'Customer question',
  examples_answer_label: 'Ideal answer',
  examples_remove: 'Remove example',
  examples_empty: 'No examples yet — 4-8 are recommended.',
  examples_counter: '{{count}} examples · 4-8 recommended',

  prompt_helper:
    '{{count}} characters · injected after the brand description and tone',

  data_add: 'Add field',
  data_remove: 'Remove field',
  data_required_label: 'Required field',
  data_empty: 'No data fields configured yet.',
  data_col_key: 'Key',
  data_col_display: 'Display name',
  data_col_type: 'Type',
  data_col_stage: 'Stage',

  data_group_label: 'Group {{index}}',
  data_group_when_prefix: 'If',
  data_group_always_active: 'Always active',
  data_group_field_count: '{{count}} fields',
  data_group_condition_hint:
    'Conditions are read-only until backend support lands.',
  data_group_empty: 'No fields in this group yet.',
  data_group_add: 'Add group',
  data_group_remove: 'Remove group',
  data_field_add: 'Add field',
  data_field_display_placeholder: 'e.g. Customer email',
  data_field_key_label: 'Key',
  data_field_key_help:
    'snake_case identifier saved in the CRM. Auto-generated from the display name; edit only if needed.',
  data_field_key_hint: 'Internal key used by the CRM and integrations.',
  data_field_type_label: 'Type',
  data_field_stage_label: 'Stage',
  data_field_options_label: 'Valid options',
  data_field_options_help:
    'List of allowed values. The agent can only save one of these.',
  data_field_options_placeholder: 'Type and press Enter to add…',
  data_field_question_label: 'Question template',
  data_field_question_help:
    'The agent uses this as a template — the LLM may paraphrase it to fit the conversation tone.',
  data_field_question_placeholder: 'e.g. What is your email address?',
  data_field_validation_label: 'Validation rule',
  data_field_validation_help:
    'Free-text rule the LLM enforces (e.g. "must contain an @").',
  data_field_validation_placeholder: 'e.g. valid email format',
  data_field_required_label: 'Required',
  data_field_required_chip: 'Required',
  data_field_required_help:
    'Must be collected before closing the conversation.',
  data_field_extractable_label: 'Extractable from media',
  data_field_extractable_help:
    'When enabled, the agent can extract this from a photo or audio (e.g. an OCR scan of a prescription).',
  data_field_advanced_toggle: 'Advanced',

  labels_add: 'Add label',
  labels_remove: 'Remove label',
  labels_empty: 'No labels configured yet.',
  labels_col_key: 'Key',
  labels_col_display: 'Display name',
  labels_col_tone: 'Tone',

  capabilities_title: 'Capabilities',
  capabilities_empty: 'No capabilities defined. Add what the agent CAN do.',
  capabilities_add: 'Add capability',
  capabilities_what_label: 'What it can do',
  capabilities_what_placeholder: 'e.g. Take orders, recommend products',
  capabilities_desc_label: 'Details',
  capabilities_desc_placeholder: 'How it does it, when, usage examples',
  limitations_title: 'Never does',
  limitations_empty: 'No limitations defined. Add what the agent must NOT do.',
  limitations_add: 'Add limit',
  limitations_what_label: 'What it CANNOT do',
  limitations_what_placeholder:
    'e.g. Schedule appointments, give medical advice',
  limitations_desc_label: 'Details',
  limitations_desc_placeholder: 'How it manifests, what to avoid exactly',
  limitations_redirect_label: 'Exact message when asked (optional)',
  limitations_redirect_placeholder:
    'e.g. "To schedule, email us at support@..."',
  limitations_redirect_hint:
    'If empty, the agent will politely refuse without promising the action.',
  common_remove: 'Remove',

  goal_milestones_title: 'Pre-CTA milestones',
  goal_milestones_add: 'Add milestone',
  goal_milestones_remove: 'Remove milestone',
  goal_opening_title: 'Conversation opener',
  goal_opening_description:
    'How the agent greets on the first turn and concrete examples.',
  goal_opening_examples_label: 'Opening examples',
  goal_opening_examples_add: 'Add example',
  goal_opening_examples_remove: 'Remove example',

  messaging_preview_label: 'Approximate preview',

  rag_enabled_title: 'Retrieval-Augmented Generation',
  rag_enabled_description:
    'When enabled, the agent searches your sources before responding.',
  rag_sources_hint: 'Manage uploaded sources from the Sources section above.',
  rag_tools_title: 'External tools',
  rag_tools_description:
    'Functions the agent can call (APIs, databases, webhooks).',
  rag_tools_empty: 'No tools connected yet.',

  handoff_enabled_title: 'Hand off to a human',
  handoff_enabled_description:
    'When enabled, the agent stops and notifies a human if any trigger matches.',
  handoff_col_type: 'Type',
  handoff_col_value: 'Trigger',
  handoff_col_reason: 'Reason',
  handoff_add: 'Add trigger',
  handoff_remove: 'Remove trigger',

  toast_created: 'Agent created.',
  toast_updated: 'Agent updated.',
  toast_duplicated: 'Agent duplicated.',
  toast_activated: 'Agent activated.',
  toast_deactivated: 'Agent deactivated.',
  toast_drafted: 'Agent moved to draft.',
  toast_undrafted: 'Agent moved out of draft.',

  wizard_step_basics: 'Basics',
  wizard_step_docs: 'Documents',
  wizard_choice_empty_title: 'Start from scratch',
  wizard_choice_empty_description:
    'Create a blank agent and configure every detail manually.',
  wizard_choice_from_docs_title: 'Generate from documents',
  wizard_choice_from_docs_description:
    'Upload 1–10 docs and we’ll pre-fill the agent for you.',
  wizard_continue: 'Continue',
  wizard_back: 'Back',
  wizard_generate_button: 'Generate agent',
  wizard_skip_to_create: 'Skip — create empty',
  wizard_files_required: 'Add at least one file to generate.',

  generation_status_queued: 'Queued…',
  generation_status_running: 'Generating your agent…',
  generation_status_failed: 'Generation failed',
  generation_status_success: 'All set!',
  generation_progress_label: '{{completed}} of {{total}} steps',

  generation_node_document_loader: 'Reading documents',
  generation_node_language_detector: 'Detecting language',
  generation_node_brand_extractor: 'Capturing brand voice',
  generation_node_domain_extractor: 'Mapping the business',
  generation_node_qa_extractor: 'Building knowledge base',
  generation_node_link_extractor: 'Collecting links',
  generation_node_data_fields_suggester: 'Choosing fields to ask for',
  generation_node_config_synthesizer: 'Composing the agent',

  generation_error_first_title: 'Something went wrong',
  generation_error_first_description:
    'We couldn’t finish generating the agent. Try again.',
  generation_error_retry_button: 'Retry',
  generation_error_persistent_title: 'Still failing',
  generation_error_persistent_description:
    'There’s a persistent issue. Wait a few minutes and try again, or open the editor and configure it manually — your draft is saved.',
  generation_error_go_to_editor_button: 'Open editor',

  toast_generation_success: 'Your agent is ready.',
  toast_generation_failed: 'Could not generate the agent.',
  toast_deleted: 'Agent deleted.',
  toast_files_pending:
    'Files were attached locally; processing will start when the backend pipeline is enabled.',

  error_display_name_taken:
    'That display name is already in use in your organization.',
  error_validation:
    'Some fields have invalid values. Review them and try again.',
  error_validation_summary:
    '{{count}} fields have invalid values. First: {{first}}',
  error_load_list: 'We could not load your agents. Please try again.',
  error_load_one: 'We could not load this agent.',
  error_load_schema: 'We could not load the configuration schema.',
  error_agent_type_not_allowed:
    'Your organization is not allowed to create agents of this type.',
  error_unknown: 'Something went wrong. Please try again.',

  create_loading_types: 'Loading available agent types…',
  create_no_types_title: 'No agent types available',
  create_no_types_description:
    'Your organization does not have any allowed agent types yet. Contact support to enable them.',

  sources_section_title: 'Source files (RAG)',
  sources_section_description:
    'Upload documents that describe your business. The agent uses them as its knowledge base on every reply.',
  sources_empty_title: 'No files yet',
  sources_empty_description:
    'Drop a PDF, DOCX, TXT, MD, CSV or XLSX to feed the agent.',
  sources_upload_hint:
    'Drag files or click to browse — PDF, DOCX, TXT, MD, CSV, XLSX. Up to 10 MB each.',
  sources_upload_active: 'Drop the files to add them',
  sources_uploading: 'Uploading {{name}}…',
  sources_pending_title: 'Ready to upload ({{count}})',
  sources_pending_remove: 'Remove file',
  sources_pending_clear: 'Remove all',
  sources_pending_upload: 'Upload {{count}}',
  sources_pending_uploading: 'Uploading…',
  sources_loading: 'Loading files…',
  sources_load_error: 'We could not load the files.',
  sources_retry: 'Retry',

  source_status_processing: 'Processing…',
  source_status_ready: 'Ready',
  source_status_failed: 'Error',
  source_qa_disabled: 'Q&A off',
  source_qa_pending: 'Q&A queued',
  source_qa_processing: 'Generating Q&A…',
  source_qa_ready: 'Q&A ready',
  source_qa_failed: 'Q&A failed',
  source_qa_label: 'Auto-generate Q&A file',
  source_qa_help:
    'We build a question/answer file from this document. Significantly improves RAG quality. (Recommended)',
  source_active_label: 'Active',
  source_active_help:
    'When disabled, the agent stops consulting this file in its answers.',
  source_pair_count: '{{count}} Q&A pairs',
  source_updated_ago: 'Updated {{when}}',

  source_action_preview: 'View file',
  source_action_preview_qa: 'View Q&A',
  source_action_regenerate_qa: 'Regenerate Q&A',
  source_action_delete: 'Delete',

  source_delete_title: 'Delete this file?',
  source_delete_description:
    'It will be removed from storage and from the agent knowledge base immediately.',
  source_delete_confirm: 'Delete file',

  toast_source_uploaded: 'File uploaded — processing in the background.',
  toast_source_updated: 'File updated.',
  toast_source_deleted: 'File deleted.',
  toast_source_qa_queued: 'Q&A regeneration queued.',
  toast_source_preview_opened: 'Opening preview…',

  error_source_file_too_large: 'The file exceeds the 10 MB limit.',
  error_source_unsupported:
    'Unsupported format. Use PDF, DOCX, TXT, MD, CSV or XLSX.',
  error_source_no_text:
    'We could not extract text from the file. Try another document.',
  error_source_qa_disabled: 'Enable Q&A before regenerating it.',
  error_source_qa_not_generated: 'This file has no Q&A generated yet.',
  error_source_upload: 'We could not upload the file. Please try again.',
  error_source_delete: 'We could not delete the file.',
  error_source_update: 'We could not update the file.',
  error_source_preview: 'We could not open the file.',

  preview_title: 'File preview',
  preview_loading: 'Loading preview…',
  preview_fetch_error: 'We could not load the file for preview.',
  preview_not_supported: 'Preview not available for this file type.',
  preview_csv_truncated:
    'Showing only the first {{count}} rows. Download the file to see everything.',
  preview_download: 'Download',

  qa_editor_title: 'Q&A pairs',
  qa_editor_pair_count: '{{count}} pairs',
  qa_editor_add_pair: 'Add pair',
  qa_editor_question_label: 'Question',
  qa_editor_answer_label: 'Answer',
  qa_editor_remove_pair: 'Remove pair',
  qa_editor_save: 'Save changes',
  qa_editor_cancel: 'Cancel',
  qa_editor_loading: 'Loading Q&A pairs…',
  qa_editor_fetch_error: 'We could not load the Q&A pairs.',
  qa_editor_empty: 'No pairs yet. Add the first one.',

  toast_qa_content_saved: 'Q&A pairs saved.',
  error_qa_content_save: 'We could not save the Q&A pairs. Please try again.',

  section_identity_title: 'Identity & brand',
  section_identity_subtitle:
    'Who the agent is, what voice it has and where it operates.',
  section_llm_title: 'Model (LLM)',
  section_llm_subtitle:
    'The brain of the agent. You can override the model for specific tasks like lead profiling.',
  section_tone_title: 'Tone & vocabulary',
  section_tone_subtitle:
    'How the agent speaks and which words to use or avoid.',
  section_examples_title: 'Style examples',
  section_examples_subtitle:
    'Question/answer pairs that illustrate the ideal tone. Used as few-shot examples.',
  section_prompt_title: 'System prompt',
  section_prompt_subtitle:
    'Extra rules appended to the system prompt — product policies, payments, conversation rules.',
  section_data_title: 'Data to collect',
  section_data_subtitle:
    'Information the agent must extract during the conversation. Define the question, stage and validation rules.',
  section_labels_title: 'Labels',
  section_labels_subtitle:
    'The agent automatically tags conversations to segment leads and trigger workflows.',
  section_capabilities_title: 'Capabilities & limits',
  section_capabilities_subtitle:
    'What the agent can do, and what it must never do. Both are injected into the system prompt.',
  section_goal_title: 'Agent goal',
  section_goal_subtitle:
    'What it tries to achieve and how aggressively it pushes toward the CTA.',
  section_messaging_title: 'Messaging style',
  section_messaging_subtitle:
    'How replies are structured: length, messages per turn and split strategy.',
  section_rag_title: 'RAG & tools',
  section_rag_subtitle:
    'Knowledge base the agent queries in real time, plus external tools it can call.',
  section_handoff_title: 'Human handoff',
  section_handoff_subtitle:
    'Conditions that pause the agent and notify a human to take over.',
  section_followup_title: 'Follow-ups',
  section_followup_subtitle:
    'When the agent re-engages a customer who stops responding or abandons.',
  section_integrations_title: 'Conversion integrations',
  section_integrations_subtitle:
    "Connect Shopify or another system to verify the agent's real purchases.",

  integration_shopify_title: 'Shopify',
  integration_shopify_description:
    'Receive paid orders from your Shopify store to confirm real conversions.',
  integration_shopify_secret_label: 'Shopify signing secret',
  integration_shopify_secret_help:
    'Paste the secret Shopify shows you when you create the webhook in Settings → Notifications.',
  integration_shopify_secret_replace_help:
    'Replacing the secret invalidates the integration until you paste a new one.',
  integration_shopify_secret_placeholder: 'Paste the Shopify signing secret',
  integration_shopify_secret_required: 'Paste the Shopify signing secret',
  integration_shopify_replace_secret: 'Replace secret',
  integration_shopify_steps_title: 'How to set it up in Shopify',
  integration_shopify_step_1:
    'Go to your Shopify Admin → Settings → Notifications.',
  integration_shopify_step_2:
    'Scroll to "Webhooks" and click "Create webhook".',
  integration_shopify_step_3:
    'Configure: Event "Order payment", Format "JSON", URL: paste the one above.',
  integration_shopify_step_4:
    'After creating the webhook, Shopify shows you a signing secret. Paste it below.',
  integration_shopify_docs_link: "View Shopify's official docs",
  integration_shop_domain_label: 'Store domain (optional)',
  integration_shop_domain_help:
    'E.g. mi-tienda.myshopify.com. Leave empty and we capture it from the first webhook.',

  integration_generic_title: 'Generic endpoint',
  integration_generic_description:
    'For Stripe, Mercado Pago, or any custom system: post conversions to our URL from your backend.',
  integration_generic_example_title: 'cURL example',

  integration_webhook_url_label: 'Webhook URL',
  integration_endpoint_url_label: 'Endpoint URL',
  integration_api_key_label: 'Your API key',
  integration_api_key_hidden_help:
    'We only show the last 4 characters for security. If you lost it, regenerate a new one.',
  integration_api_key_generate: 'Generate API key',
  integration_api_key_rotate: 'Regenerate API key',
  integration_api_key_generated: 'API key generated',
  integration_save: 'Save',
  integration_saved: 'Integration saved',
  integration_save_failed: 'Could not save the integration',
  integration_disable: 'Disable',
  integration_disabled: 'Integration disabled',
  integration_copy: 'Copy',
  integration_copied: 'Copied!',
  integration_status_active: 'Active',
  integration_status_configured_no_traffic: 'No traffic',
  integration_status_inactive: 'Not configured',

  credential_modal_title: 'Your credential',
  credential_modal_description:
    "Copy it now — for security you won't be able to see it again.",
  credential_modal_warning:
    "Save it somewhere safe. If you lose it you'll need to regenerate a new one.",
  credential_modal_copy: 'Copy',
  credential_modal_copied: 'Copied',
  credential_modal_close: 'Close',

  list_verified_conversions_label: 'Verified conversions',
  list_verified_revenue_label: 'Revenue',
  list_verified_revenue_mixed: 'multi-currency',
  list_verified_revenue_mixed_tooltip:
    "Conversions came in different currencies — the total isn't converted.",
  list_verified_empty_no_data: 'Waiting for first conversion',
  list_verified_empty_no_integration:
    'Connect an integration to see real purchases',
  list_stat_goal_achieved: 'Goal',
  list_stat_goal_achieved_tooltip:
    'Percentage of conversations where the agent concluded the goal was met (sell, qualify, book, etc.). It is the LLM\'s read of the chat — useful but subjective. For real purchases, see "Verified".',
  list_stat_convs_tooltip:
    'Unique conversations in the last 7 days. The chart shows the daily trend.',
  list_stat_verified_short: 'Verified',
  list_stat_verified_tooltip_data:
    'Conversations with at least one purchase verified by an external integration (Shopify, generic endpoint). Ground truth — not LLM opinion.',
  list_verified_tooltip_revenue: 'Revenue',
  list_verified_tooltip_sources: 'By source',
  section_links_title: 'Links',
  section_links_subtitle:
    'Official URLs the agent can share with the customer when needed.',

  field_display_name_label: 'Display name',
  field_brand_name_label: 'Brand name',
  field_persona_name_label: 'Agent persona name',
  field_persona_name_help: 'How the agent introduces itself in conversations.',
  field_brand_description_label: 'Description',
  field_brand_description_help:
    'Brand context injected into the system prompt.',
  field_default_language_label: 'Default language',
  field_timezone_label: 'Timezone',
  field_llm_provider_label: 'Provider',
  field_llm_model_label: 'Model',
  field_llm_model_help:
    'Model identifier per provider (e.g. gpt-4o-mini, claude-sonnet-4-5).',
  field_llm_temperature_label: 'Temperature',
  field_llm_temperature_help: '0 = deterministic, 1 = creative.',
  field_tone_description_label: 'Tone description',
  field_tone_description_help:
    'Free-text describing the personality. Injected into the system prompt.',
  field_preferred_words_label: 'Preferred words',
  field_preferred_words_help: 'Brand vocabulary the agent should prioritize.',
  field_forbidden_words_label: 'Forbidden words',
  field_forbidden_words_help: 'Always avoided and blocked from responses.',
  field_qa_examples_label: 'Q&A examples',
  field_qa_examples_help:
    'Pairs that show the ideal tone. Used as few-shot examples.',
  field_qa_question_label: 'Customer question',
  field_qa_answer_label: 'Ideal answer',
  field_system_prompt_label: 'System prompt (extra)',
  field_system_prompt_help: 'Injected after the brand description and tone.',
  field_data_fields_label: 'Data to collect',
  field_data_fields_help:
    'Groups of fields with optional conditions that branch the conversation.',
  field_available_labels_label: 'Available labels',
  field_agent_capabilities_label: 'Capabilities',
  field_agent_limitations_label: 'Limits (never does)',
  field_goal_type_label: 'Goal type',
  field_goal_primary_outcome_label: 'Primary outcome',
  field_goal_primary_outcome_help: 'What "success" means for this agent.',
  field_goal_aggressiveness_label: 'CTA aggressiveness',
  field_goal_aggressiveness_help: 'How directly the agent asks for the sale.',
  field_goal_min_turns_label: 'Minimum turns before CTA',
  field_goal_min_turns_help:
    'The agent never asks for the sale before this many messages.',
  field_goal_milestones_label: 'Pre-CTA milestones',
  field_goal_milestones_help:
    'Conditions that must be true before the CTA fires.',
  field_goal_opening_style_label: 'Opening style',
  field_goal_opening_examples_label: 'Opening examples',
  field_messaging_conciseness_label: 'Conciseness',
  field_messaging_conciseness_help: 'How brief the agent replies.',
  field_messaging_split_strategy_label: 'Split strategy',
  field_messaging_max_messages_label: 'Max messages per turn',
  field_messaging_target_chars_label: 'Target characters per message',
  field_messaging_image_cooldown_label: 'Image cooldown (minutes)',
  field_messaging_image_cooldown_help:
    'Minimum minutes between two image-bearing replies.',
  field_rag_enabled_label: 'Retrieval-Augmented Generation',
  field_rag_enabled_help:
    'When enabled, the agent searches your sources before responding.',
  field_tools_enabled_label: 'External tools',
  field_tools_enabled_help:
    'Functions the agent can call (APIs, databases, webhooks).',
  field_handoff_enabled_label: 'Hand off to a human',
  field_handoff_enabled_help:
    'When enabled, the agent stops and notifies a human if any trigger matches.',
  field_handoff_triggers_label: 'Triggers',
  field_handoff_message_label: 'Handoff message',
  field_handoff_message_help:
    'Message shown to the customer when the agent escalates.',
  quiet_hours_default_title: 'Default quiet hours',
  quiet_hours_default_help:
    'Time window when no follow-ups will be sent. Individual rules can override.',
  quiet_hours_using_custom: 'Using a custom override',
  rule_new_default_name: 'New rule',
  rule_add_custom: 'Add rule',
  rule_remove: 'Remove rule',
  rule_empty: 'No rules configured yet. Add one to get started.',
  rule_badge_builtin: 'Built-in',
  field_followup_master_enabled_label: 'Enable follow-ups',
  field_followup_master_enabled_help:
    'When off, no follow-up fires for this agent.',
  field_quiet_hours_default_enabled_label: 'Apply default quiet hours',
  field_quiet_hours_enabled_label: 'Apply quiet hours',
  field_quiet_hours_start_label: 'Start',
  field_quiet_hours_end_label: 'End',
  field_quiet_hours_days_label: 'Days',
  field_quiet_hours_override_label: 'Override quiet hours for this rule',
  field_quiet_hours_override_help:
    "If off, this rule inherits the agent's default quiet hours.",
  field_use_default_quiet_label: 'Use default',
  field_followup_rules_label: 'Follow-up rules',
  field_followup_rules_help:
    'Built-in rules can be disabled but not deleted. You can also add custom rules.',
  field_rule_name_label: 'Name',
  field_rule_enabled_label: 'Enable this rule',
  field_delay_hours_label: 'Wait (hours)',
  field_delay_hours_help: 'The clock pauses during quiet hours.',
  field_cancel_if_goal_met_label:
    "Don't send if the agent already met its goal",
  field_cancel_if_goal_met_help:
    "Recommended. If the customer already did what the agent was after (e.g. purchased), this reminder won't be sent.",
  field_message_override_enabled_label: 'Customize message',
  field_message_override_enabled_help:
    'When off, the AI writes the message based on brand voice and context.',
  field_message_text_label: 'Message',
  field_message_text_help:
    'Available variables: {{customer_name}}, {{brand_name}}, {{rule_name}}, {{cart_count}}, {{cart_total}}.',
  field_message_text_placeholder:
    "Hi {{customer_name}}, did you get a chance to think it over? I'm here to help.",
  rule_summary_after: 'After',
  rule_summary_skip_if_goal: 'skips if goal met',
  day_sun: 'Sun',
  day_mon: 'Mon',
  day_tue: 'Tue',
  day_wed: 'Wed',
  day_thu: 'Thu',
  day_fri: 'Fri',
  day_sat: 'Sat',
  field_links_privacy_label: 'Privacy policy',
  field_links_terms_label: 'Terms of service',
  field_links_help_label: 'Help / contact',
  field_links_about_label: 'About the brand',
  field_links_payment_label: 'Payment base URL',
  field_links_catalog_label: 'Catalog / main product',

  field_links_label: 'Brand links',
  field_links_help:
    'URLs the agent can share with the customer. Each link has a description telling the agent what it is and when to send it.',
  field_link_url_label: 'URL',
  field_link_description_label: 'Description',
  field_link_description_help:
    'What this link is and when to send it. The agent reads this to decide whether to share the link in a given conversation.',
  field_link_description_placeholder:
    'e.g. Refund policy — share when the customer asks about returns within 30 days.',
  field_link_cta_label: 'CTA label override',
  field_link_cta_help:
    'Exact button text to use for this link. Leave empty to let the LLM generate one (≤20 chars).',
  field_link_cta_placeholder: 'e.g. Pay now',
  field_link_type_label: 'Type',
  field_link_type_help: 'Helps the agent prioritize and group links. Optional.',
  field_link_type_placeholder: 'Pick a type…',
  field_link_type_product: 'Product',
  field_link_type_payment: 'Payment',
  field_link_type_privacy_policy: 'Privacy policy',
  field_link_type_terms: 'Terms',
  field_link_type_help_option: 'Help / contact',
  field_link_type_about: 'About',
  field_link_type_faq: 'FAQ',
  field_link_type_social: 'Social',
  field_link_type_other: 'Other',
  field_optional_suffix: 'optional',
  links_item_title: 'Link {{index}}',
  links_add: 'Add link',
  links_remove: 'Remove link',
  links_empty: 'No links yet — add one for the agent to share.',
  links_open: 'Open link',

  goal_type_sell_label: 'Sell',
  goal_type_sell_desc: 'Drive the conversation toward a purchase.',
  goal_type_support_label: 'Support',
  goal_type_support_desc: 'Resolve doubts and issues.',
  goal_type_qualify_label: 'Qualify',
  goal_type_qualify_desc: 'Collect data for the sales team.',
  goal_type_book_label: 'Book',
  goal_type_book_desc: 'Drive toward booking an appointment or demo.',
  goal_type_prospect_label: 'Prospect',
  goal_type_prospect_desc: 'Surface and qualify potential customers.',

  list_summary: '{{active}} active · {{paused}} paused',
  list_filter_label: 'Filter agents',
  list_filter_all: 'All',
  list_filter_active: 'Active',
  list_filter_paused: 'Paused',
  list_filter_draft: 'Drafts',
  list_search_placeholder: 'Search by agent or brand name…',
  list_search_loading: 'Searching…',
  list_search_error: 'Could not run search. Try again.',
  list_sort_label: 'Sort by',
  list_sort_updated: 'Recently updated',
  list_sort_name: 'Name',
  list_view_label: 'View mode',
  list_view_grid: 'Grid view',
  list_view_list: 'List view',
  list_col_agent: 'Agent',
  list_col_status: 'Status',
  list_col_channels: 'Channels',
  list_col_volume: 'Volume',
  list_col_trend: 'Trend',
  list_no_channels: 'No channels',
  list_no_match_title: 'No agents match',
  list_no_match_description: 'Try a different filter or create a new one.',
  list_stat_convs: 'Conv. (7d)',
  list_stat_convs_short: 'conversations',
  list_stat_conversion: 'Conversion',
  list_action_test: 'Test',
  list_action_configure: 'Configure',
  list_footer_hint_title: 'Want a second opinion on your best agent?',
  list_footer_hint_description:
    '{{brand}} AI can review the conversations and suggest concrete improvements to tone, common objections and pre-CTA milestones.',
  list_estimate_label: 'Est.',
  list_estimate_tooltip:
    'Sample-based estimate — high-volume agent. Backed by a 30% sample, extrapolated.',
  list_stale_label: 'Stale data',
  list_stale_tooltip:
    "No new conversations in the last few hours, so the rollup hasn't refreshed.",
  list_traffic_label: 'Traffic source',
  list_traffic_organic: 'Organic',
  list_traffic_ads: 'Ads',
  list_traffic_empty: 'No traffic yet',

  // Playground (chat with your agent, no channel attached)
  playground_title: 'Test agent',
  playground_subtitle: 'Chat with your agent without going through WhatsApp. Same agent, same config — only the channel differs.',
  playground_open: 'Test',
  playground_empty_title: 'Send the first message',
  playground_empty_body: 'Talk like a customer would. The agent opens according to its configured goal.',
  playground_reset: 'Reset conversation',
  playground_reset_done: 'Conversation reset',
  playground_reset_failed: 'Could not reset the conversation',
  playground_send_failed: 'The agent could not reply',
  playground_empty_reply: 'The agent returned no text on this turn.',
  playground_trace_title: 'Turn trace',
  playground_trace_nodes: 'Nodes',
  playground_trace_intent: 'Intent',
  playground_trace_tone: 'Lead tone',
  playground_trace_tools: 'Tools',
  playground_trace_empty: 'Send a message to see the trace.',
  playground_human_active: 'A human took over this thread.',
  playground_disclaimer: 'Actions are real: whatever the agent creates is persisted.',
  playground_test_mode: 'Test mode',
  playground_send: 'Send',
  playground_placeholder: 'Type as a customer would…',
};
